<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DealsKanbanCard from './DealsKanbanCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  deals: { type: Array, default: () => [] },
  selectable: { type: Boolean, default: false },
  selectedDealIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'deal-click',
  'drop',
  'create',
  'toggle-select',
  'toggleStage',
  'dragStart',
  'dragEnd',
]);

const { t } = useI18n();

const totalValue = computed(() => {
  const sum = props.deals.reduce((acc, d) => acc + Number(d.value || 0), 0);
  if (!sum) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(sum);
});

const selectedCountInStage = computed(
  () => props.deals.filter(d => props.selectedDealIds.includes(d.id)).length
);

const isStageFullySelected = computed(
  () =>
    props.deals.length > 0 && selectedCountInStage.value === props.deals.length
);

const isStagePartiallySelected = computed(
  () => selectedCountInStage.value > 0 && !isStageFullySelected.value
);

const onDragOver = event => {
  if (props.selectable) return;
  event.preventDefault();
};

const onDrop = event => {
  if (props.selectable) return;
  event.preventDefault();
  const dealId = event.dataTransfer.getData('dealId');
  if (dealId) emit('drop', { stageId: props.stage.id, dealId: Number(dealId) });
};

const onDragStart = (event, deal) => {
  if (props.selectable) {
    event.preventDefault();
    return;
  }
  event.dataTransfer.setData('dealId', String(deal.id));
  emit('dragStart', deal.id);
};

const onToggleStage = checked => {
  emit('toggleStage', { stageId: props.stage.id, value: checked });
};
</script>

<template>
  <div
    class="flex flex-col gap-2 min-w-[240px] w-[240px] flex-shrink-0 max-h-full"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <div
      class="flex items-center justify-between px-1 pb-1 border-b-2"
      :style="{ borderColor: stage.color || '#6366f1' }"
    >
      <div class="flex items-center gap-1.5 min-w-0">
        <Checkbox
          v-if="selectable"
          :model-value="isStageFullySelected"
          :indeterminate="isStagePartiallySelected"
          class="shrink-0"
          @change="event => onToggleStage(event.target.checked)"
        />
        <span
          class="w-2 h-2 rounded-full flex-shrink-0"
          :style="{ backgroundColor: stage.color || '#6366f1' }"
        />
        <span
          class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide truncate"
        >
          {{ stage.name }}
        </span>
        <span
          v-if="totalValue"
          class="text-xs font-medium text-green-700 flex-shrink-0"
        >
          {{ totalValue }}
        </span>
      </div>
      <div class="flex items-center gap-1 flex-shrink-0">
        <span class="text-xs text-n-slate-9">{{ deals.length }}</span>
        <Button
          icon="i-lucide-plus"
          variant="ghost"
          size="xs"
          color="slate"
          :title="t('DEALS.KANBAN.ADD_DEAL_TO_STAGE')"
          @click="emit('create', stage)"
        />
      </div>
    </div>
    <div
      class="flex flex-col gap-2 flex-1 min-h-[120px] overflow-y-auto thin-scrollbar rounded-xl p-2 bg-n-alpha-1 border border-n-weak border-dashed transition-colors"
    >
      <div
        v-for="deal in deals"
        :key="deal.id"
        :draggable="!selectable"
        @dragstart="onDragStart($event, deal)"
        @dragend="emit('dragEnd')"
      >
        <DealsKanbanCard
          :deal="deal"
          :selectable="selectable"
          :is-selected="selectedDealIds.includes(deal.id)"
          @click="emit('deal-click', deal)"
          @select="value => emit('toggle-select', { id: deal.id, value })"
        />
      </div>
    </div>
  </div>
</template>
